<template>
  <layout-full-card
    :back-fallback="backFallback"
    id="assignment-explore-view"
    :breadcrumbs="[$__('nav.home'), $__('assignments.explore.title')]"
    :loading="loading"
  >
    <template #card-header>
      <jz-title>
        <jz-icon :src="bookIcon" />
        {{ "assignments.explore.title" | __ }}
      </jz-title>
    </template>

    <jz-container class="filters">
      <jz-container id="filter-status">
        <jz-input-dropdown
          v-model="selectedStatusIdx"
          container-selector="layout-full-card"
          :options="statusFilterOptions"
          :icon="bookIcon"
          :label="'assignments.explore.status-filter.title' | __"
        />
      </jz-container>

      <jz-input-dropdown
        id="filter-year"
        v-model="selectedYear"
        container-selector="layout-full-card"
        :options="yearFilterOptions"
        :icon="calendarIcon"
        :label="'assignments.explore.year-filter.title' | __"
      />

      <jz-container id="filter-month" flex column>
        <div class="title">{{ "assignments.explore.month-selector.title" | __ }}</div>

        <div class="buttons">
          <button
            v-for="month in schoolYearMonths"
            :key="month"
            :disabled="!assignmentMonths.includes(month)"
            :data-month="month"
            :class="[{ overdue: monthsWithOverdue.includes(month) }]"
            @click="scrollToMonth"
          >
            {{ month }} {{ "assignments.explore.month" | __ }}
          </button>
        </div>
      </jz-container>
    </jz-container>

    <div class="assignment-cards-container">
      <jz-container
        v-if="!loading && assignmentsByReverseOrder.length <= 0"
        class="empty-assignments-notice"
        >{{ "assignments.no-assignments" | __ }}</jz-container
      >
      <template v-for="month in assignmentsByReverseOrder">
        <div
          :id="`month-${month[0].dueDate.getMonth() + 1}`"
          :key="month[0].dueDate.getMonth() + 1"
          class="month-header"
        >
          {{
            "assignment.explore.date-separator"
              | __(month[0].dueDate.getFullYear(), month[0].dueDate.getMonth() + 1)
          }}
        </div>

        <jz-container :key="month[0].dueDate.getMonth() + 1" class="assignment-cards">
          <assignment-card
            v-for="assignment in month"
            :key="assignment.id"
            :assignment="assignment"
          />
        </jz-container>
      </template>
    </div>
  </layout-full-card>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import AssignmentCard from "@tt-app/components/assignment/AssignmentCard";
import LayoutFullCard from "@tt-app/layouts/LayoutFullCard";

import backIcon from "@joyz/app-core/assets/icons/black/Back.svg";
import bookIcon from "@joyz/app-core/assets/icons/default/Book.svg";
import calendarIcon from "@joyz/app-core/assets/icons/default/Calendar.svg";
import groupIcon from "@joyz/app-core/assets/icons/transparent/Group.svg";

import {
  ASSIGNMENT_STATUS_FILTER_GROUPS,
  ROUTES,
  VUEX_FORM_MUTATION_NAMES,
  VUEX_FORM_NAMESPACE,
  VUEX_ASSIGNMENT_ACTION_NAMES,
  VUEX_ASSIGNMENT_NAMESPACE
} from "@tt-app/constants";
import { schoolYearFromDate } from "@tt-app/utils";

export default {
  name: "AssignmentExplore",
  components: { AssignmentCard, LayoutFullCard },

  data() {
    return {
      loading: false,
      selectedStatusIdx: "0",
      selectedYear: "0"
    };
  },

  computed: {
    ...mapState({
      assignments: state => state.assignment.list,
      selectedStatusFilterIndex: state => state.form.assignment.selectedStatusFilterIndex.value,
      selectedYearFilter: state => state.form.assignment.selectedYearFilter.value
    }),
    ...mapGetters({
      assignmentMonths: "assignment/assignmentMonths",
      assignmentYears: "assignment/assignmentYears",
      groupedAssignments: "assignment/groupedAssignments",
      monthsWithOverdue: "assignment/monthsWithOverdue"
    }),
    assignmentsByReverseOrder() {
      let assignmentsByMonth = [...this.schoolYearMonths]
        .reverse()
        .map(month => this.groupedAssignments[month]);

      assignmentsByMonth = assignmentsByMonth.filter(Boolean);

      return assignmentsByMonth;
    },
    backFallback() {
      return {
        name: ROUTES.HOME.NAME
      };
    },
    statusFilterOptions() {
      return Object.entries(this.assignmentStatusFilters).map(([statusIndex]) => ({
        label: this.$__(`assignments.explore.status-filter.${statusIndex}`),
        value: statusIndex
      }));
    },
    schoolYearMonths() {
      return ["4", "5", "6", "7", "8", "9", "10", "11", "12", "1", "2", "3"];
    },
    yearFilterOptions() {
      return this.assignmentYears.map(year => ({
        label: year,
        value: year
      }));
    }
  },

  watch: {
    selectedStatusIdx: {
      handler(newStatusIdx) {
        this.handleStatusFilterChange(newStatusIdx);
      },
      immediate: false
    },
    selectedYear: {
      handler(newYear) {
        this.handleYearFilterChange(-1, newYear);
      },
      immediate: false
    }
  },

  created() {
    this.backIcon = backIcon;
    this.bookIcon = bookIcon;
    this.calendarIcon = calendarIcon;
    this.groupIcon = groupIcon;
    this.assignmentDetailsRouteName = ROUTES.ASSIGNMENT.DETAILS.NAME;
    this.assignmentStatusFilters = ASSIGNMENT_STATUS_FILTER_GROUPS;
    this.fetchInitialData();
    this.selectedYear = schoolYearFromDate();
  },
  updated() {
    this.scrollToCurrentMonth();
  },
  methods: {
    handleStatusFilterChange(selectedIndex) {
      this.handleFilterChange({
        filterName: "selectedStatusFilterIndex",
        filterValue: selectedIndex
      });
    },
    handleYearFilterChange(selectedIndex, value) {
      this.handleFilterChange({
        filterName: "selectedYearFilter",
        filterValue: value
      });
    },
    async fetchInitialData() {
      this.loading = true;

      await this.$store.dispatch(
        `${VUEX_ASSIGNMENT_NAMESPACE}/${VUEX_ASSIGNMENT_ACTION_NAMES.LIST_REQUEST}`
      );

      this.loading = false;
    },
    scrollToCurrentMonth() {
      const currentDate = new Date();

      // If the current year is selected, scroll to the current month
      // otherwise scroll to April
      if (this.selectedYearFilter === `${currentDate.getFullYear()}`) {
        const month = `${currentDate.getMonth() + 1}`;

        this.scrollToMonth({ target: { dataset: { month } } });
      } else {
        this.scrollToMonth({ target: { dataset: { month: "4" } } });
      }
    },
    statusText(status) {
      if ([3, 5, 6].includes(status)) {
        return "完了済み：";
      }
    },
    statusDates(assignment) {
      if ([3, 5, 6].includes(assignment.status)) {
        const submittedDate = new Date(assignment.submitted);
        const month = submittedDate.getMonth() + 1;
        const day = submittedDate.getDate();
        return `${month}/${day}`;
      } else {
        const startDate = new Date(assignment.startDate);
        const startMonth = startDate.getMonth() + 1;
        const startDay = startDate.getDate();
        const dueDate = new Date(assignment.dueDate);
        const dueMonth = dueDate.getMonth() + 1;
        const dueDay = dueDate.getDate();
        return `${startMonth}/${startDay} ~ ${dueMonth}/${dueDay}`;
      }
    },
    handleFilterChange({ filterName, filterValue }) {
      this.$store.commit(`${VUEX_FORM_NAMESPACE}/${VUEX_FORM_MUTATION_NAMES.UPDATE_FORM}`, {
        formName: "assignment",
        field: filterName,
        value: filterValue
      });
    },
    scrollToMonth(event) {
      const {
        target: {
          dataset: { month }
        }
      } = event;

      const element = document.getElementById(`month-${month}`);

      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }
};
</script>

<style lang="scss">
#assignment-explore-view.layout-full-card {
  padding: $gutter ($gutter / 2) ($gutter / 2) ($gutter / 2);

  .jz-card.content {
    margin: 0 0 ($gutter / 2) 0;
    max-width: 920px;

    .filters {
      background-color: var(--color-primary-alternate-background-light);
      border-bottom: 1px solid rgba(var(--rgb-black), 0.08);
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
      display: grid;
      gap: $gutter;
      grid-template-columns: auto;
      grid-template-rows: auto;
      grid-template-areas: "status-filter" "year-filter" "month-filter";
      padding: $gutter;

      #filter-status {
        grid-area: status-filter;
      }

      #filter-year {
        grid-area: year-filter;
      }

      #filter-month {
        grid-area: month-filter;
        justify-content: space-around;

        .title {
          color: var(--color-secondary);
          font-size: $font-size-xs;
          font-weight: bold;
        }

        .buttons {
          button {
            background-color: var(--color-white);
            border-radius: 4px;
            color: var(--color-dark);
            font-size: $font-size-xs;
            font-weight: bold;
            min-width: 40px;
            padding: $gutter / 4;

            &:disabled {
              color: var(--color-secondary);
              cursor: default;
            }

            &:not(:last-child) {
              margin: ($gutter / 2) ($gutter / 2) 0 0;
            }

            &.overdue:before {
              border-radius: 50%;
              background-color: var(--color-danger);
              content: "";
              display: inline-block;
              height: 8px;
              width: 8px;
            }
          }
        }
      }
    }

    .assignment-cards-container {
      .empty-assignments-notice {
        align-items: center;
        margin: ($gutter * 2) $gutter;
        text-align: center;

        @include is-text-description($font-size-sm);
      }

      .month-header {
        background-color: var(--color-light-background-light);
        color: var(--color-dark);
        font-size: $font-size-sm;
        padding: 8px 24px;
      }

      .assignment-cards {
        display: grid;
        gap: $gutter;
        grid-template-columns: repeat(1, auto);
        padding: $gutter;
      }
    }
  }
}

@include responsive(tablet) {
  #assignment-explore-view.layout-full-card {
    padding: $gutter $gutter 0 $gutter;

    .jz-card.content {
      display: flex;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      margin: 0;

      .filters {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: auto;
        grid-template-areas: "status-filter year-filter" "month-filter month-filter";
      }

      .assignment-cards-container {
        .assignment-cards {
          grid-template-columns: repeat(2, 1fr);
        }
      }
    }
  }
}

@include responsive(desktop) {
  #assignment-explore-view.layout-full-card {
    .jz-card.content {
      .filters {
        grid-template-columns: 194px 194px auto;
        grid-template-rows: auto;
        grid-template-areas: "status-filter year-filter month-filter";
      }

      .assignment-cards-container {
        overflow: auto;
        .assignment-cards {
          grid-template-columns: repeat(3, 1fr);
        }
      }
    }
  }
}
</style>
